import { CommentDetailProps } from "./commentDetail";
import { useTranslation } from "react-i18next";
import { AuthContext } from "core/contexts/auth/auth";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getAlertDetailsByIdService,
  refetchAlertDetailsByIdService,
} from "modules/ac2/services/alertService";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { IconX, IconMaximize, IconMinimize } from "@tabler/icons-react";
import { setPreviewWidth, setSideModal } from "modules/ac2/store/alert";

import {
  AlertSummaryContainer,
  TagsButton,
  TextContainer,
  CommentsContainer,
  AlertDetailsContainer,
  PanelContainer,
  MarkdownContainer,
  ActionButtonsContainer,
  AWSCLIContainer,
  TerraformContainer,
  ReprocessButton,
  HeaderContainer,
} from "./alertSummary.styled";
import {
  AccordionSummary,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AlertComments from "./alertComments";
import { useTheme } from "styled-components";
import CustomMarkdown from "core/components/global/markdown/markdown";
import StandaloneChatInterface from "./StandaloneChatInterface";

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export interface IRelatedAlerts {
  id: string;
  description?: string;
  createdDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number | string;
  value: number | string;
}

interface AlertDetailsAccordionProps {
  assigned?: string[];
  tags?: any[];
  org?: string;
  lastModified: string;
  createdDate: string;
}

export function AlertDetailsAccordion({
  assigned,
  tags,
  org,
  lastModified,
  createdDate,
}: AlertDetailsAccordionProps) {
  return (
    <div className="w-100 p-0">
      <AlertDetailsContainer className="shadow-xl my-2">
        <AccordionSummary
          component={Paper}
          elevation={1}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography>Alert Details</Typography>
        </AccordionSummary>
        <TableContainer component={Paper} elevation={2}>
          <Table width={690} aria-label="alert-details">
            <TableBody>
              <TableRow
                key={0}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  AWS Org
                </TableCell>
                {org ? (
                  <TableCell component="th" scope="row">
                    {org}
                  </TableCell>
                ) : (
                  <TableCell></TableCell>
                )}
              </TableRow>
              <TableRow
                key={2}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  Assigned User(s)
                </TableCell>
                <TableCell component="th" scope="row">
                  {assigned?.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </TableCell>
              </TableRow>
              <TableRow
                key={3}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  Last Modified
                </TableCell>
                <TableCell component="th" scope="row">
                  {lastModified}
                </TableCell>
              </TableRow>
              <TableRow
                key={4}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  Created
                </TableCell>
                <TableCell component="th" scope="row">
                  {createdDate}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AlertDetailsContainer>
    </div>
  );
}

export interface StandardMapping {
  Object(any);
}

export interface AlertSummarySchema {
  id: string;
  severity: string;
  status: string;
  classification: string;
  organization?: string;
  assignedUsers?: string[];
  description: string;
  standardMapping: any;
  awsBestPractice: string;
  terraformRemediation: string;
  awsCliRemediation: any;
  cloudformationTemplate: string;
  createdDate: string;
  lastModified: string;
  comments: CommentDetailProps[];
  findingid: string;
  tags?: any[];
  relatedAlerts?: any[];
  viewStatus?: string; // Added viewStatus to track the status of the alert view
}

export interface AlertSummaryProps {
  setOpenSideModal: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
}

interface alertRefreshLoadingStates {
  description?: boolean;
  standardMapping?: boolean;
  awsBestPractice?: boolean;
  awsCliRemediation?: boolean;
  cloudformationTemplate?: boolean;
  terraformRemediation?: boolean;
}

export const AlertSummary = ({
  setOpenSideModal,
  selectedId,
}: AlertSummaryProps) => {
  const theme = useTheme();
  const { afUser: user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [alertSummary, setAlertSummary] = useState<AlertSummarySchema>();
  const [isLoadingGeneral, setIsLoadingGeneral] = useState(true);
  const [loadingStates, setLoadingStates] =
    useState<alertRefreshLoadingStates>();
  const [errorState, setErrorState] = useState(false);
  const [notFoundState, setNotFoundState] = useState(false);
  const { checkAuthToken } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState({
    actions: 0,
    response: 0,
    gap_analysis: "",
  });
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const dispatch = useAppDispatch();
  const alertState = useAppSelector((state) => state.alerts);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [prevWidth, setPrevWidth] = useState(50);
  const [findingId, setFindingId] = useState<string | undefined>(undefined);

  function getAWSProvider(resourceId) {
    if (resourceId !== undefined && resourceId !== null && resourceId !== "") {
      switch (true) {
        case resourceId.startsWith("arn:aws:"):
          return "AWS";
        case /^(organizations|projects|folders)\//.test(resourceId):
          return "GCP";
        case resourceId.startsWith("/subscriptions/"):
          return "Azure";
        default:
          return "Unknown IaC Service";
      }
    } else {
      return "Unknown IaC Service";
    }
  }

  function getIacService(resourceId) {
    if (resourceId !== undefined && resourceId !== null && resourceId !== "") {
      switch (true) {
        case resourceId.startsWith("arn:aws:"):
          return "CloudFormation";
        case /^(organizations|projects|folders)\//.test(resourceId):
          return "Google Deployment Manager";
        case resourceId.startsWith("/subscriptions/"):
          return "Azure Resource Manager";
        default:
          return "Unknown IaC Service";
      }
    } else {
      return "Unknown IaC Service";
    }
  }

  /**
   * On component initiation, the data for this will be fetched
   */
  useEffect(() => {
    const fetchData = async (id) => {
      const token = await checkAuthToken();
      const config = {
        Authorization: token,
        "Content-Type": "application/json",
      };
      try {
        setNotFoundState(false);
        getAlertDetailsByIdService(config, id)
          .then((response) => {
            if (response) {
              if (
                response.standardMapping === null ||
                response.awsBestPractice === null ||
                response.terraformRemediation === null ||
                response.awsCliRemediation === null ||
                response.description === null ||
                response.cloudformationTemplate === null
              ) {
                setErrorState(true);
              } else {
                setAlertSummary(response);
                setErrorState(false);
              }
            } else {
              setErrorState(true);
            }
            setIsLoadingGeneral(false);
          })
          .catch((error) => {
            setIsLoadingGeneral(false);
            setErrorState(true);
          });
      } catch (error) {
        setIsLoadingGeneral(false);
        setErrorState(true);
      }
    };

    setAlertSummary(undefined);
    fetchData(selectedId);
  }, [selectedId]);

  /**
   * Either point to a new tab to show or point to no tab
   * 0 will show no sections, any other number with an assoicated
   * section will show that section
   * @param newValue
   */
  const handleTabChange = (newValue: number | string, type: string) => {
    if (type in tabValue) {
      if (newValue === tabValue[type]) {
        setTabValue((current) => ({
          ...current,
          [type]: 0,
        }));
      } else {
        setTabValue((current) => ({
          ...current,
          [type]: newValue,
        }));
      }
    }
  };

  // Find the row in pageEvents with the selectedId and get its findingId
  const getFindingIdForSelected = (selectedId, pageEvents) => {
    const found = pageEvents.find((event) => event.id === selectedId);
    if (found !== undefined) {
      setFindingId(found.findingId);
    }
    return found ? found.findingId : undefined;
  };

  const RefetchAlertDetails = async (id) => {
    setIsLoadingGeneral(true);
    setErrorState(false);

    // Reset all loading states to true
    setLoadingStates({
      description: true,
      standardMapping: true,
      awsBestPractice: true,
      awsCliRemediation: true,
      cloudformationTemplate: true,
      terraformRemediation: true,
    });

    try {
      const token = await checkAuthToken();

      // Define a callback function to handle incremental updates
      const updateCallback = (mappedData) => {
        console.log("Received incremental update:", mappedData);

        // Update the alert summary with the latest data
        setAlertSummary((current) => {
          // If we don't have a current state yet, initialize with the mappedData
          if (!current) return mappedData;

          // Otherwise, merge the new data with the existing data
          return { ...current, ...mappedData };
        });

        // Update loading states for sections that are now available
        setLoadingStates((current) => {
          const newState = { ...current };

          // For each field in mappedData, if it has a value, mark it as not loading
          if (mappedData.description) newState.description = false;
          if (mappedData.standardMapping) newState.standardMapping = false;
          if (mappedData.awsBestPractice) newState.awsBestPractice = false;
          if (mappedData.awsCliRemediation) newState.awsCliRemediation = false;
          if (mappedData.cloudformationTemplate)
            newState.cloudformationTemplate = false;
          if (mappedData.terraformRemediation)
            newState.terraformRemediation = false;

          return newState;
        });
      };

      // Call the service with the update callback
      const response = await refetchAlertDetailsByIdService(
        token,
        id,
        updateCallback
      );

      if (response&&response.viewStatus!=="notfound") {
        console.log("Refetched Alert Summary Response:", response);
        // Final update with the complete response
        setAlertSummary(response);
        setErrorState(false);

        // Mark all sections as loaded
        setLoadingStates({
          description: false,
          standardMapping: false,
          awsBestPractice: false,
          awsCliRemediation: false,
          cloudformationTemplate: false,
          terraformRemediation: false,
        });
      } else {
        if(response.viewStatus==="notfound"){
        setNotFoundState(true);
        
       setLoadingStates({
          description: false,
          standardMapping: false,
          awsBestPractice: false,
          awsCliRemediation: false,
          cloudformationTemplate: false,
          terraformRemediation: false,
        });
      } else {
        setErrorState(true);
      }
    }
    } catch (error) {
      console.error("Error in RefetchAlertDetails:", error);
      setErrorState(true);
    } finally {
      setIsLoadingGeneral(false);
    }
  };

  const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{ width: "inherit" }}
        {...other}
      >
        {value === index && <PanelContainer>{children}</PanelContainer>}
      </div>
    );
  };

  useEffect(() => {
    const copyText = async () => {
      if (copiedText) {
        const success = await copyToClipboard(copiedText);
        if (!success) {
          console.log(
            "Copy failed, no permission or clipboard support, update your browser permissions."
          );
        }
      }
    };

    copyText();
  }, [copiedText, copyToClipboard]);

  const [statusColor, setStatusColor] = useState(Object());

  const statusMapping = {
    [t("STATUS.OPEN")]: {
      bk: theme.colors.open_incident,
      text: theme.colors.textColor,
    },
    [t("STATUS.IN_PROGRESS")]: {
      bk: theme.colors.in_progress_incident,
      text: theme.colors.textColor,
    },
    [t("STATUS.CLOSED")]: {
      bk: theme.colors.closed_incident,
      text: theme.colors.textColor,
    },
    [t("STATUS.REQUEST_TUNING")]: {
      bk: theme.colors.requested_incident,
      text: theme.colors.textColor,
    },
  };

  useEffect(() => {
    if (alertSummary?.status && statusMapping[alertSummary?.status]) {
      setStatusColor({
        text: statusMapping[alertSummary?.status].text,
        bk: statusMapping[alertSummary?.status].bk,
      });
    }
  }, [alertSummary?.status]);

  interface ToggleFullWidthProps {
    close?: boolean;
  }

  // Toggle between full width and previous width
  const toggleFullWidth = ({ close }: ToggleFullWidthProps = {}) => {
    try {
      if (close) {
        // Set the side modal to closed
        dispatch(setSideModal(false));
      } else {
        if (isFullWidth) {
          // Return to previous width using Redux
          if (prevWidth > 10) {
            dispatch(setPreviewWidth(prevWidth));
          } else {
            dispatch(setPreviewWidth(30)); // Default width if previous width is too small
          }
        } else {
          // Save current width before going full width
          setPrevWidth(alertState.previewWidth);

          // Set to maximum width (95%) using Redux
          dispatch(setPreviewWidth(100));
        }
        setIsFullWidth(!isFullWidth);
      }
    } catch (error) {
      console.error("Error in toggleFullWidth:", error);
    }
  };

  return (
    <AlertSummaryContainer container>
      {errorState === false &&notFoundState==false&& (
        <div className="d-flex justify-content-between w-100 align-items-center py-4">
          <div className="d-flex align-items-center">
            <strong className="text-break">
              {t("INCIDENTS_PAGE.INCIDENT")}:{" "}
              {alertSummary?.findingid || findingId || ""}
            </strong>

            {(!alertSummary?.findingid && !findingId) && (
              <Skeleton
                className="ms-2"
                style={{ minWidth: "100px", height: 36 }}
                animation="wave"
                variant="text"
              />
            )}
          </div>
          <div className="d-flex">
            <button
              onClick={() => toggleFullWidth()}
              style={{ color: theme.colors.textColor }}
              className={`p-2 rounded-full transition-colors`}
              title="Toggle width"
            >
              {isFullWidth ? (
                <IconMinimize size={18} />
              ) : (
                <IconMaximize size={18} />
              )}
            </button>
            <button
              onClick={() => toggleFullWidth({ close: true })}
              style={{ color: theme.colors.textColor }}
              className={`p-2 rounded-full transition-colors`}
              title="Close"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>
      )}

      {/*       
      {alertSummary !== undefined && <>
          <div className="d-flex flex-row py-4">
            { alertSummary ?
              <>
                <div style={{backgroundColor: statusColor ? statusColor.bk : "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{alertSummary?.status}</strong>
                </div>
                <div style={{backgroundColor: theme.severity.range[alertSummary ? alertSummary.severity : ""]}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{t("INCIDENTS_PAGE.SEVERITY")}: {alertSummary?.severity}</strong>
                </div>
              </> :
              <>
                <div style={{backgroundColor: "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>Status</strong>
                </div>
                <div style={{backgroundColor: "#7b7881"}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{t("INCIDENTS_PAGE.SEVERITY")}</strong>
                </div>
              </>
            }
          </div>
        </>
      } */}

      {errorState === false && notFoundState === false && (
        <>
          <AlertDetailsAccordion
            assigned={alertSummary?.assignedUsers}
            tags={alertSummary?.tags}
            org={alertSummary?.organization}
            lastModified={alertSummary?.lastModified || ""}
            createdDate={alertSummary?.createdDate || ""}
          />
        </>
      )}
{notFoundState === true &&
        <div className="flex h-full w-full items-center justify-center flex-col p-6 text-center py-4">
          <div className="mb-4">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h3 className="text-xl font-medium mb-3">No Alert Data Found</h3>
          <p className="text-sm text-gray-500">
            Data was not found in data lake.
          </p>
        </div>
      }
      {notFoundState===false&&errorState === true ? (
        <div className="flex h-full w-full items-center justify-center flex-col p-6 text-center py-4">
          <div className="mb-4">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h3 className="text-xl font-medium mb-3">No Alert Data Found</h3>
          <p className="text-sm text-gray-500">
            We couldn't retrieve the data for this alert.
          </p>
          <ReprocessButton
            onClick={() => {
              setIsLoadingGeneral(true); // Show spinner immediately

              RefetchAlertDetails(
                getFindingIdForSelected(selectedId, alertState.pageEvents)
              );
            }}
          >
            Reprocess Alert
          </ReprocessButton>

          {isLoadingGeneral && (
            <div className="d-flex justify-content-center align-items-center py-4">
              <Skeleton
                style={{ width: 150, height: 52 }}
                animation="wave"
                variant="text"
              />
              <Skeleton
                className="ms-2"
                style={{ width: 150, height: 52 }}
                animation="wave"
                variant="text"
              />
            </div>
          )}
        </div>
      ) : alertSummary?.description && alertSummary?.description !== "" ? (
        <TextContainer
          container
          spacing={2}
          className="d-flex flex-column py-4"
        >
          <strong className="mb-3" style={{ fontSize: theme.font.large }}>
            Description:
          </strong>
          {/* <Markdown>{alertSummary.description}</Markdown> */}
          <CustomMarkdown
            content={alertSummary.description}
            showEditButton={false}
          />
        </TextContainer>
      ) : (
        (isLoadingGeneral ||
          (loadingStates !== undefined && loadingStates.description)) && (
          <>
            <Skeleton
              style={{ width: 150, height: 52 }}
              animation="wave"
              variant="text"
            />
            <Skeleton
              className="w-100"
              style={{ height: 150 }}
              animation="wave"
              variant="text"
            />
          </>
        )
      )}

      {errorState === false &&notFoundState==false&&
      alertSummary?.awsBestPractice &&
      alertSummary?.awsBestPractice !== "" ? (
        <TextContainer
          container
          spacing={2}
          className="d-flex flex-column py-4"
        >
          <strong
            className="mb-3"
            style={{ fontSize: theme.font.large }}
          >{`${getAWSProvider(
            alertSummary?.findingid
          )} Best Practice:`}</strong>
          <CustomMarkdown
            content={alertSummary.awsBestPractice}
            showEditButton={false}
          />
        </TextContainer>
      ) : (
        (isLoadingGeneral ||
          (loadingStates !== undefined && loadingStates.awsBestPractice)) && (
          <>
            <Skeleton
              style={{ width: 150, height: 52 }}
              animation="wave"
              variant="text"
            />
            <Skeleton
              className="w-100"
              style={{ height: 150 }}
              animation="wave"
              variant="text"
            />
          </>
        )
      )}

      {errorState === false &&notFoundState==false&& alertSummary !== undefined && (
        <ActionButtonsContainer className="mb-2 pb-4">
          <HeaderContainer
            container
            spacing={2}
            className="d-flex flex-wrap flex-column py-3"
          >
            <strong style={{ fontSize: theme.font.large }}>
              {t("INCIDENTS_PAGE.REMEDIATION_ACTIONS")}:
            </strong>
            <div className="d-flex flex-wrap flex-row pt-2">
              {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.GENERATE_REMEDIATION")}
          </TagsButton> */}
              {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.DETECTION_RULE")}
          </TagsButton> */}
              {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.AWS_MANAGEMENT_CONSOLE")}
          </TagsButton> */}
              <TagsButton
                onClick={() => handleTabChange(4, "actions")}
                disabled={
                  alertSummary?.awsCliRemediation
                    ? false
                    : isLoadingGeneral ||
                      (loadingStates !== undefined &&
                        loadingStates.awsCliRemediation)
                    ? true
                    : true
                }
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: "#A100FF",
                  color: "#ffffff",
                  borderRadius: "8px",
                  border: "none",
                  height: "38px",
                  fontSize: theme.font.medium,
                }}
              >
                {`${getAWSProvider(alertSummary?.findingid)} CLI`}
              </TagsButton>
              <TagsButton
                onClick={() => handleTabChange(5, "actions")}
                disabled={
                  alertSummary?.cloudformationTemplate
                    ? false
                    : isLoadingGeneral ||
                      (loadingStates !== undefined &&
                        loadingStates.cloudformationTemplate)
                    ? true
                    : true
                }
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: "#A100FF",
                  color: "#ffffff",
                  borderRadius: "8px",
                  border: "none",
                  height: "38px",
                  fontSize: theme.font.medium,
                }}
              >
                {getIacService(alertSummary?.findingid)}
              </TagsButton>
              <TagsButton
                onClick={() => handleTabChange(6, "actions")}
                disabled={
                  alertSummary?.terraformRemediation
                    ? false
                    : isLoadingGeneral ||
                      (loadingStates !== undefined &&
                        loadingStates.terraformRemediation)
                    ? true
                    : true
                }
                variant="contained"
                className="p-3 m-2"
                style={{
                  backgroundColor: "#A100FF",
                  color: "#ffffff",
                  borderRadius: "8px",
                  border: "none",
                  height: "38px",
                  fontSize: theme.font.medium,
                }}
              >
                {t("INCIDENTS_PAGE.TERRAFORM")}
              </TagsButton>
              {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.GENERATE_TICKET")}
          </TagsButton> */}
            </div>
          </HeaderContainer>

          {alertSummary?.awsCliRemediation &&
          alertSummary?.awsCliRemediation !== "" ? (
            <TabPanel value={tabValue["actions"]} index={4}>
              <MarkdownContainer className="py-2 ps-2 pe-2">
                <AWSCLIContainer elevation={1} className="p-3">
                  {alertSummary.awsCliRemediation}
                </AWSCLIContainer>
              </MarkdownContainer>
            </TabPanel>
          ) : (
            tabValue["actions"] === 4 &&
            (isLoadingGeneral ||
              (loadingStates !== undefined &&
                loadingStates.awsCliRemediation)) && (
              <TabPanel value={tabValue["actions"]} index={4}>
                <MarkdownContainer className="py-2 ps-2 pe-2">
                  <Skeleton
                    className="w-100"
                    style={{ height: 150 }}
                    animation="wave"
                    variant="text"
                  />
                </MarkdownContainer>
              </TabPanel>
            )
          )}
          {alertSummary?.cloudformationTemplate &&
          alertSummary?.cloudformationTemplate !== "" ? (
            <TabPanel value={tabValue["actions"]} index={5}>
              <div className="py-2 ps-2 pe-2">
                <CustomMarkdown
                  content={alertSummary.cloudformationTemplate}
                  showEditButton={false}
                />
              </div>
            </TabPanel>
          ) : (
            tabValue["actions"] === 5 &&
            (isLoadingGeneral ||
              (loadingStates !== undefined &&
                loadingStates.cloudformationTemplate)) && (
              <TabPanel value={tabValue["actions"]} index={5}>
                <div className="py-2 ps-2 pe-2">
                  <Skeleton
                    className="w-100"
                    style={{ height: 150 }}
                    animation="wave"
                    variant="text"
                  />
                </div>
              </TabPanel>
            )
          )}
          {alertSummary?.terraformRemediation &&
          alertSummary?.terraformRemediation !== "" ? (
            <TabPanel value={tabValue["actions"]} index={6}>
              <MarkdownContainer className="py-2 ps-2 pe-2">
                <TerraformContainer>
                  <CustomMarkdown
                    content={alertSummary.terraformRemediation}
                    showEditButton={false}
                  />
                </TerraformContainer>
              </MarkdownContainer>
            </TabPanel>
          ) : (
            tabValue["actions"] === 6 &&
            (isLoadingGeneral ||
              (loadingStates !== undefined &&
                loadingStates.terraformRemediation)) && (
              <TabPanel value={tabValue["actions"]} index={6}>
                <MarkdownContainer className="py-2 ps-2 pe-2">
                  <TerraformContainer>
                    <Skeleton
                      className="w-100"
                      style={{ height: 150 }}
                      animation="wave"
                      variant="text"
                    />
                  </TerraformContainer>
                </MarkdownContainer>
              </TabPanel>
            )
          )}
        </ActionButtonsContainer>
      )}

      {/* Add Gap Analysis section */}
      {errorState === false &&
      alertSummary?.standardMapping &&
      alertSummary?.standardMapping !== "" ? (
        <TextContainer
          container
          spacing={2}
          className="d-flex flex-column py-4"
        >
          <strong className="mb-3" style={{ fontSize: theme.font.large }}>
            {t("INCIDENTS_PAGE.GAP_ANALYSIS")}:
          </strong>
          <CustomMarkdown
            content={alertSummary.standardMapping}
            showEditButton={false}
          />
        </TextContainer>
      ) : (
        (isLoadingGeneral ||
          (loadingStates !== undefined && loadingStates.standardMapping)) && (
          <>
            <Skeleton
              style={{ width: 150, height: 52 }}
              animation="wave"
              variant="text"
            />
            <Skeleton
              className="w-100"
              style={{ height: 150 }}
              animation="wave"
              variant="text"
            />
          </>
        )
      )}

      {errorState === false && notFoundState === false && (
        <CommentsContainer
          className="py-4"
          style={{ display: "block", width: "100%" }}
        >
          <AlertComments
            t={t}
            theme={theme}
            initialComments={alertSummary?.comments || []}
            alertId={alertSummary?.id || ""}
            user={user}
          />
        </CommentsContainer>
      )}

      {errorState === false &&notFoundState === false && (
        <div className="py-4">
          <strong style={{ fontSize: theme.font.large }}>
            {t("INCIDENTS_PAGE.CONVERSE_WITH_AI_AC2")}:
          </strong>

          <StandaloneChatInterface
            theme={theme}
            alertId={alertSummary?.findingid}
          />

          <HeaderContainer
            container
            spacing={2}
            className="d-flex flex-column my-2"
          >
            <strong className="mb-3" style={{ fontSize: theme.font.large }}>
              {t("INCIDENTS_PAGE.AI_DISCLAMER_TITLE")}:
            </strong>
            <p className="word-break">
              <strong>{t("INCIDENTS_PAGE.PLEASE_NOTE")}: </strong>
              The information provided here has been generated by AI and, as
              such, may contain inaccuracies or &#39;hallucinations&#39;. We
              advise users to verify the accuracy of the information before
              relying on it for any critical decisions or actions.
            </p>
          </HeaderContainer>
        </div>
      )}
    </AlertSummaryContainer>
  );
};

export default AlertSummary;
